import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Hook to check if the EPC-QTD engine is enabled system-wide.
 * Uses emp_motor_flags table (system-wide flags, not cluster-scoped).
 * 
 * Returns { enabled, isLoading } where:
 * - enabled=true: EPC-QTD views (emp_vw_evm_contrato etc.) should be consumed
 * - enabled=false: fallback to emp_vw_indicadores_blindados
 */
export function useEpcQtdFlag() {
  const { data, isLoading } = useQuery({
    queryKey: ['epc-qtd-flag'],
    queryFn: async () => {
      try {
        const { data: flag, error } = await (supabase as any)
          .from('emp_motor_flags')
          .select('ativo')
          .eq('flag_nome', 'epc_qtd_enabled')
          .maybeSingle();

        if (error) {
          console.warn('[EVM:FLAG] EPC_QTD flag query error, defaulting OFF:', error.message);
          return false;
        }

        const enabled = flag?.ativo === true;
        console.debug('[EVM:FLAG] EPC_QTD_ENABLED:', enabled);
        return enabled;
      } catch {
        console.warn('[EVM:FLAG] EPC_QTD flag exception, defaulting OFF');
        return false;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 min cache
  });

  return {
    enabled: data ?? false,
    isLoading,
  };
}

/**
 * Check if a specific contract uses the EPC-QTD engine.
 * A contract uses EPC-QTD when:
 * 1. epc_qtd_enabled flag is ON
 * 2. tipo_contrato_operacional is 'preco_global' (EPC) 
 *    OR tipo_medicao is 'epc' or 'hibrido'
 */
export function isContractEpcQtd(
  epcQtdEnabled: boolean,
  tipoContratoOperacional?: string,
  tipoMedicao?: string,
): boolean {
  if (!epcQtdEnabled) return false;
  return (
    tipoContratoOperacional === 'preco_global' ||
    tipoMedicao === 'epc' ||
    tipoMedicao === 'hibrido'
  );
}
